"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import type { CSSProperties, ElementType } from "react";

interface Heading1Props {
  data?: {
    data?: {
      content?: string;
      heading_tag?: string;
      font_family?: string;
      desktop_font_size?: number | string;
      mobile_font_size?: number | string;
      font_weight?: number | string;
      line_height?: string;
      text_color?: string;
      background_color?: string;
      word_break?: boolean | number | string;
      status?: boolean | number | string;
    };
  };
}

const ALLOWED_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;
type HeadingTag = (typeof ALLOWED_TAGS)[number];

/** Clamp a font size into the admin-restricted 14–32px range, with a fallback. */
function normalizeFontSize(
  value: number | string | undefined,
  fallback: number
): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(32, Math.max(14, Math.round(parsed)));
}

export default function Heading1({ data }: Heading1Props) {
  const sectionData = data?.data || (data as any);
  // useIsMobile returns null pre-hydration → treat as desktop so SSR matches.
  const isMobile = useIsMobile(768) === true;

  if (!sectionData) {
    return null;
  }

  const isActive =
    sectionData.status === true ||
    sectionData.status === 1 ||
    sectionData.status === "1";

  if (!isActive) {
    return null;
  }

  const content = sectionData.content?.trim() || "";
  if (!content) {
    return null;
  }

  const tag: HeadingTag = ALLOWED_TAGS.includes(
    sectionData.heading_tag as HeadingTag
  )
    ? (sectionData.heading_tag as HeadingTag)
    : "h2";
  const Tag: ElementType = tag;

  const fontSize = isMobile
    ? normalizeFontSize(sectionData.mobile_font_size, 24)
    : normalizeFontSize(sectionData.desktop_font_size, 32);

  const shouldBreakWord =
    sectionData.word_break === true ||
    sectionData.word_break === 1 ||
    sectionData.word_break === "1" ||
    sectionData.word_break === undefined;

  const headingStyle: CSSProperties = {
    margin: 0,
    fontFamily:
      sectionData.font_family?.trim() ||
      "var(--font-poppins), Poppins, sans-serif",
    fontSize: `${fontSize}px`,
    fontWeight: sectionData.font_weight
      ? Number(sectionData.font_weight) || sectionData.font_weight
      : 600,
    lineHeight: sectionData.line_height?.trim() || "1.2",
    color: sectionData.text_color || "#111111",
    ...(shouldBreakWord
      ? { wordBreak: "break-word", overflowWrap: "anywhere" }
      : {}),
  };

  return (
    <section
      className="w-full px-5 py-6 sm:px-[60px] md:px-[80px] lg:px-[100px] xl:px-[160px]"
      style={{ backgroundColor: sectionData.background_color || "#FFFFFF" }}
    >
      <div className="mx-auto w-full max-w-[1750px]">
        <Tag style={headingStyle}>{content}</Tag>
      </div>
    </section>
  );
}
